import { useEffect, useState } from "react";
import { X, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "__popup_dismissed_v2";

export default function WelcomePopup() {
    const [popup, setPopup] = useState(null);
    const [visible, setVisible] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Skip if already dismissed this session
        try {
            if (sessionStorage.getItem(SESSION_KEY) === "1") return;
        } catch {}

        const apply = (data) => {
            const p = data && data.popup ? data.popup : null;
            if (p) {
                setPopup(p);
                setVisible(true);
                try {
                    localStorage.setItem("__popup_last_id", p.id);
                } catch {}
            }
        };
        const lastId = (() => {
            try {
                return localStorage.getItem("__popup_last_id") || "";
            } catch {
                return "";
            }
        })();
        const prefetched = typeof window !== "undefined" && window.__popupPromise;
        if (prefetched) window.__popupPromise = null;
        (prefetched || axios.get(`${API}/popup`, { params: { exclude: lastId } }).then((r) => r.data))
            .then(apply)
            .catch(() => {});
    }, []);

    const close = () => {
        setVisible(false);
        try {
            sessionStorage.setItem(SESSION_KEY, "1");
        } catch {}
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(popup.code);
            setCopied(true);
            // Count code copy as engagement (same weight as link click).
            axios.post(`${API}/popup/click/${popup.id}`).catch(() => {});
            toast.success("تم نسخ الكود");
            setTimeout(() => setCopied(false), 1400);
        } catch {}
    };

    const onCta = () => {
        if (!popup?.link) return;
        try {
            axios.post(`${API}/popup/click/${popup.id}`).catch(() => {});
        } catch {}
    };

    if (!visible || !popup) return null;

    return (
        <div
            data-testid="welcome-popup"
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in"
            onClick={close}
        >
            <div
                className="relative bg-white rounded-lg max-w-md w-full shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                dir="rtl"
            >
                <button
                    data-testid="welcome-popup-close"
                    onClick={close}
                    aria-label="إغلاق"
                    className="absolute top-2 end-2 z-10 bg-black/70 hover:bg-black text-white rounded-full p-1.5 shadow-lg transition-colors"
                >
                    <X size={20} />
                </button>

                {popup.image_url && (
                    <div className="w-full aspect-video bg-zinc-100">
                        <img
                            src={popup.image_url}
                            alt={popup.title || ""}
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}

                <div className="p-6 space-y-3">
                    {popup.title && (
                        <h3 className="font-heading text-2xl font-bold text-black leading-tight">
                            {popup.title}
                        </h3>
                    )}
                    {popup.description && (
                        <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-line">
                            {popup.description}
                        </p>
                    )}
                    <div className="flex flex-col gap-2 pt-2">
                        {popup.code && (
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-xs font-bold text-zinc-700">كود المنتج</span>
                                <button
                                    onClick={copyCode}
                                    data-testid="welcome-popup-code"
                                    className="inline-flex items-center justify-center gap-2 bg-[#F5FBFA] border-2 border-dashed border-[#4FB8B0] rounded px-4 py-2 font-mono font-bold text-[#1E5E5A] hover:bg-[#4FB8B0]/10 w-full"
                                >
                                    {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                                    <span>{popup.code}</span>
                                </button>
                            </div>
                        )}
                        {popup.link && (
                            <a
                                data-testid="welcome-popup-cta"
                                href={popup.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={onCta}
                                className="inline-flex items-center justify-center gap-2 bg-[#1E5E5A] hover:bg-[#0F2A28] text-white font-bold px-4 py-3 rounded transition-colors"
                            >
                                <ExternalLink size={16} />
                                زيارة الموقع
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
